import { newReleases } from '@/mocks/books';
import { getBookPublicationBadge, getBookPublicationBadgeClass, getBookPublicationBadgeLabel } from '../../../../../shared/book-publication-status';

const topicMap: Record<string, { label: string; className: string }> = {
  'AIGC 工業設計 實戰課': {
    label: 'AIGC 彩色書籍',
    className: 'bg-accent-100/80 text-accent-800 border-accent-200/60',
  },
  'AIGC 互動設計實戰課': {
    label: 'AIGC 彩色書籍',
    className: 'bg-accent-100/80 text-accent-800 border-accent-200/60',
  },
  '色彩的物競天擇，生命沒那麼理所當然': {
    label: '科普',
    className: 'bg-secondary-100/80 text-secondary-800 border-secondary-200/60',
  },
  '冷血動物形貌史，魚類、兩棲與爬行類的演化變形記': {
    label: '自然科學',
    className: 'bg-secondary-100/80 text-secondary-800 border-secondary-200/60',
  },
  '書途萬里，中華出版發展史': {
    label: '歷史',
    className: 'bg-primary-100/80 text-primary-800 border-primary-200/60',
  },
  '紅樓眾生相，被忽略的「小人物」敘事': {
    label: '文化與人文',
    className: 'bg-primary-100/80 text-primary-800 border-primary-200/60',
  },
};

function getTopicBadge(title: string) {
  const topic = topicMap[title];
  if (!topic) return null;
  return (
    <span
      className={`inline-block text-[10px] tracking-[0.05em] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${topic.className}`}
    >
      {topic.label}
    </span>
  );
}

export default function NewReleases() {
  return (
    <section className="w-full min-h-screen flex flex-col justify-center py-20 md:py-28 bg-foreground-950">
      <div className="w-full px-6 md:px-10">
        {/* ── Section Header ── */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16 max-w-[1200px] mx-auto">
          <div>
            <span className="inline-block text-xs tracking-[0.15em] text-primary-300 px-3 py-1 border border-primary-500/20 rounded-full mb-4">
              2026 最新發行
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-background-50 mb-3">
              2026 最新發行
            </h2>
            <p className="text-sm text-foreground-400 max-w-lg leading-relaxed">
              探索清文華泉於 2026 年最新發行的彩色知識、科普與歷史類書籍。
            </p>
          </div>
        </div>

        {/* ── Book Cards ── */}
        <div className="overflow-x-auto pb-4 max-w-[1200px] mx-auto">
          <div className="flex gap-5 md:gap-6 min-w-max px-1">
            {newReleases.map((book) => {
              const topicBadge = getTopicBadge(book.title);
              const publicationBadge = getBookPublicationBadge(book.publishDate);
              const catalogSearchUrl = `https://catalog.sonbook.net/?s=${encodeURIComponent(book.isbn)}`;

              return (
                <div
                  key={book.isbn}
                  className="group flex-shrink-0 w-[220px] md:w-[240px] flex flex-col"
                >
                  {/* Cover */}
                  <a
                    href={catalogSearchUrl}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-4 block cursor-pointer"
                  >
                    <img
                      src={book.coverImage}
                      alt={book.title}
                      title={`${book.title} - 清文華泉`}
                      className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-105"
                    />
                    {publicationBadge && (
                      <div className="absolute top-2 left-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap ${getBookPublicationBadgeClass(publicationBadge)}`}>
                          {getBookPublicationBadgeLabel(publicationBadge)}
                        </span>
                      </div>
                    )}
                  </a>

                  {/* Topic Badge */}
                  {topicBadge && (
                    <div className="mb-2">{topicBadge}</div>
                  )}

                  {/* Title */}
                  <h3 className="text-sm md:text-[15px] font-medium text-background-50 group-hover:text-primary-300 transition-colors duration-200 line-clamp-3 mb-1.5 leading-snug">
                    {book.title}
                  </h3>

                  {/* Author */}
                  <p className="text-xs text-foreground-400 mb-1">{book.author}</p>

                  {/* ISBN reference */}
                  <p className="text-[11px] text-foreground-500/70 mb-3">
                    ISBN {book.isbn}
                  </p>

                  {/* Book Info Link */}
                  <a
                    href={catalogSearchUrl}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="mt-auto inline-flex items-center gap-1.5 text-xs text-primary-300 hover:text-primary-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <span>查看書籍資訊</span>
                    <i className="ri-arrow-right-up-line text-[10px]"></i>
                  </a>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Section Footer ── */}
        <div className="mt-12 md:mt-16 pt-8 border-t border-foreground-800/40 max-w-[1200px] mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs text-foreground-500 leading-relaxed max-w-lg">
                完整書名、作者、內容主題及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
              </p>
            </div>
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              前往書籍資料庫
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
